import type { IRecentLeaveRequests } from "@/interface/dashboard.interface";
import { formatDate, getInitials } from "@/utility/helper";
import { useNavigate } from "react-router-dom";

const RenderLeaveTable: React.FC<{
  title: string;
  leaves: IRecentLeaveRequests[];
  detailPath?: string;
}> = ({ title, leaves, detailPath }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="font-display text-sm font-semibold text-slate-800">
          {title}
        </h3>
      </div>
      <div className="p-2 sm:p-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Employee
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Dates
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {leaves.length > 0 ? (
                leaves.map((leave) => {
                  const status = (leave.status || "pending").toLowerCase();
                  let statusClasses =
                    "text-amber-600 border-amber-600 bg-amber-50";
                  if (status === "approved" || status === "active") {
                    statusClasses =
                      "text-green-600 border-green-600 bg-green-50";
                  } else if (status === "rejected" || status === "cancelled") {
                    statusClasses =
                      "text-destructive border-destructive bg-destructive/10";
                  }
                  return (
                    <tr
                      key={leave.id}
                      className={`group ${detailPath ? "cursor-pointer" : ""}`}
                      onClick={() =>
                        detailPath && navigate(`${detailPath}/${leave.id}`)
                      }
                    >
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-l-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-bold shrink-0">
                            {getInitials(leave.employee?.name)}
                          </div>
                          <span className="text-slate-700 font-medium">
                            {leave.employee?.name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 text-slate-600 group-hover:bg-slate-50/80">
                        {leave.type || "N/A"}
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 text-slate-500 text-xs group-hover:bg-slate-50/80">
                        {formatDate(leave.startDate)} →{" "}
                        {formatDate(leave.endDate)}
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-r-lg">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold border rounded-full capitalize ${statusClasses}`}
                        >
                          {leave.status || "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    No recent leave requests
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RenderLeaveTable;
